import React from "react";
import {
  Zap,
  ShieldCheck,
  Video,
  ScanEye,
  Lock,
  Fingerprint,
  KeyRound,
  Boxes,
  ShieldAlert,
  Check,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card, Badge } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { PageHero, FeatureCard, CTASection, WhatsAppButton } from "../../components/marketing/Shared";

function FlowMockup() {
  const rows = [
    { m: "POST", p: "/liveness/start", s: "session_token" },
    { m: "302", p: "→ verify_url", s: "hosted check" },
    { m: "302", p: "→ redirect_uri", s: "code · passed" },
    { m: "POST", p: "/liveness/result", s: "verdict" },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-neutral-950 p-3 shadow-2xl">
      <div className="mb-2.5 flex items-center justify-between px-1">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ctf">
          <Lock className="h-3.5 w-3.5" /> step-up · redirect flow
        </span>
        <span className="rounded bg-ctf/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-ctf">HMAC</span>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 rounded-md bg-white/[0.04] px-2.5 py-2">
            <span className="w-10 shrink-0 rounded bg-ctf/15 text-center font-mono text-[9px] font-bold text-ctf">{r.m}</span>
            <span className="flex-1 truncate font-mono text-[11px] text-white/80">{r.p}</span>
            <span className="font-mono text-[10px] text-white/40">{r.s}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between rounded-lg bg-black/50 px-3 py-1.5">
        <span className="font-mono text-[10px] text-ctf">● liveness + deep PAD</span>
        <span className="font-mono text-[10px] text-white/40">verdict immutable</span>
      </div>
    </div>
  );
}

export default function Auth() {
  const { t } = useI18n();
  const features = [
    { icon: Zap, k: "stepup" },
    { icon: ShieldAlert, k: "takeover" },
    { icon: ScanEye, k: "deepfake" },
    { icon: Video, k: "injection" },
    { icon: ShieldCheck, k: "gate" },
    { icon: Fingerprint, k: "deep" },
    { icon: KeyRound, k: "webhook" },
  ];

  return (
    <>
      <PageHero
        breadcrumb={t("nav.auth")}
        accent="ctf"
        eyebrow={t("stepup.hero.eyebrow")}
        title={t("stepup.hero.title")}
        lead={t("stepup.hero.lead")}
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contato" size="lg" className="bg-ctf text-white hover:brightness-110">
            {t("common.bookDemo")}
          </Button>
          <WhatsAppButton variant="outline" className="border-white/25 text-white hover:bg-white/10" />
        </div>
      </PageHero>

      {/* Flow showcase */}
      <Section>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Badge tone="ctf">{t("stepup.hero.eyebrow")}</Badge>
              <h2 className="mt-4 text-3xl">{t("stepup.showcase.title")}</h2>
              <p className="mt-4 text-muted-foreground">{t("stepup.showcase.desc")}</p>
              <ul className="mt-6 space-y-3">
                {["stepup", "gate", "webhook"].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-ctf" />
                    <span className="text-sm">
                      <strong className="font-semibold">{t(`stepup.f.${k}.title`)}</strong> — {t(`stepup.f.${k}.desc`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <FlowMockup />
          </div>
        </Container>
      </Section>

      {/* Features grid */}
      <Section alt>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <FeatureCard key={f.k} icon={f.icon} accent="ctf" title={t(`stepup.f.${f.k}.title`)} desc={t(`stepup.f.${f.k}.desc`)} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Two audiences */}
      <Section>
        <Container>
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ctf/12 text-ctf">
                <Boxes className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-xl">{t("stepup.audience.apps")}</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {[t("stepup.f.stepup.desc"), t("stepup.f.webhook.desc"), t("stepup.f.gate.desc")].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ctf" /> {x}
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ctf/12 text-ctf">
                <ShieldAlert className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-xl">{t("stepup.audience.security")}</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {[t("stepup.f.deepfake.desc"), t("stepup.f.injection.desc"), t("stepup.f.deep.desc")].map((x, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ctf" /> {x}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
